import React, { useState } from 'react';
import { styles } from './HeaderLayout.css';
import { stylesLocalised } from './HeaderLayoutLocalised.css';
import { HeaderUtilities } from './components/HeaderUtilities/HeaderUtilities';
import { HeaderUtilityButtonProps } from './components/HeaderUtilityButton/HeaderUtilityButton';
import { HeaderMenuButton } from './components/HeaderMenuButton/HeaderMenuButton';
import { HeaderJbLogo } from './components/HeaderJbLogo/HeaderJbLogo';
import { QuickSearch } from './components/QuickSearch/QuickSearch';
import { TrackingProvider, useTrackingContext } from '../Tracking';
import { HeaderNavigation, HeaderNavigationProps } from './components/HeaderNavigation/HeaderNavigation';
import { IdPrefixProvider } from '../IdPrefix';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { CarouselMainSnackbars } from '../CarouselMainSnackbars/CarouselMainSnackbars';
import { CountDownSnackbar } from '../CountDownSnackbar/CountDownSnackbar';
import { SubSnackbar } from '../SubSnackbars/SubSnackbars';
import { GroupedSnackbarsProps } from '../Contentful/transformers/SnackbarTransformer';

export type HeaderLayoutProps = {
  utilities: Array<HeaderUtilityButtonProps>;
  isLocalisationEnabled?: boolean;
  LocalisationHeader?: React.ReactElement;
  headerNavigationProps?: HeaderNavigationProps;
  snackbarProps?: GroupedSnackbarsProps;
  logoSubtitle?: string;
};

export const HeaderLayout = ({
  utilities,
  isLocalisationEnabled,
  LocalisationHeader,
  headerNavigationProps,
  snackbarProps,
  logoSubtitle,
}: HeaderLayoutProps) => {
  const appliedStyles = isLocalisationEnabled ? stylesLocalised : styles;
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const { tracker } = useTrackingContext();
  const isDesktop = useBreakpoint('md', 'up');

  const handleToggleNavigation = () => {
    tracker.trackCustomEvent({
      event: `custom.menu.${isNavigationOpen ? 'close' : 'open'}`,
      group: tracker.group,
      type: isNavigationOpen ? 'close' : 'open',
    });
    setIsNavigationOpen(!isNavigationOpen);
  };

  const { main = [], sub = [], countdown = [] } = snackbarProps || {};

  return (
    <TrackingProvider
      prefix="custom"
      context="menu"
      group={isDesktop ? 'Mega Nav' : 'mobile-menu'}
      trackPageLoad={false}
    >
      <div className={classNames(appliedStyles.container, styles.wrapper({ isNavigationOpen }))}>
        {main?.length ? <CarouselMainSnackbars items={main} /> : null}
        <div className={appliedStyles.contentContainer} id="header-content-container">
          <IdPrefixProvider prefix="header">
            <HeaderMenuButton isOpen={isNavigationOpen} onClick={handleToggleNavigation} />
            <HeaderJbLogo subtitle={logoSubtitle} />
            <QuickSearch />
            {isLocalisationEnabled && LocalisationHeader ? LocalisationHeader : null}
            <HeaderUtilities utilities={utilities} />
          </IdPrefixProvider>
        </div>
        {headerNavigationProps ? (
          <div className={styles.navigation({ isOpen: isNavigationOpen })} id="header-nav-container">
            <HeaderNavigation {...headerNavigationProps} />
          </div>
        ) : null}
      </div>
      {sub?.length ? (
        <div id="snackbar-sub" data-testid="snackbar-sub">
          <SubSnackbar subSnackbars={sub} />{' '}
        </div>
      ) : null}

      {countdown.length ? (
        <div id="snackbar-countdown" data-testid="snackbar-countdown">
          <CountDownSnackbar {...countdown[0]} />
        </div>
      ) : null}
    </TrackingProvider>
  );
};
